'use client';

import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef } from 'react';

import CustomSlider from '@/components/Hero/CustomSlider';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { BreadcrumbItem } from '@/lib/breadcrumbs/types';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { BrandingHeroDto } from './types';

type VideoSources = {
	mov?: string;
	webm?: string;
	mp4?: string;
	poster?: string;
	mobileImg?: string;
};

const VIDEO_SOURCE_META: { key: keyof VideoSources; type: string }[] = [
	{ key: 'mov', type: 'video/quicktime; codecs="hvc1"' },
	{ key: 'webm', type: 'video/webm' },
	{ key: 'mp4', type: 'video/mp4' }
];

const BRANDING_VIDEO_SOURCES: VideoSources = {
	webm: '/assets/video/webm/branding-page/hero.webm',
	poster: '/assets/images/branding-page/hero-poster.webp'
};

const CREATIVE_MARKETING_PARTNERSHIP_VIDEO_SOURCES: VideoSources = {
	webm: '/assets/video/webm/creative-marketing-partnership/cmp-hero.webm',
	poster: '/assets/images/creative-marketing-partnership/cmp-poster.webp',
	mobileImg: '/assets/images/creative-marketing-partnership/bg-cmp-mobile.webp'
};

interface Props {
	data: BrandingHeroDto;
	breadcrumbItems?: BreadcrumbItem[];
}

const BrandingHero = ({ data, breadcrumbItems }: Props) => {
	const variant = data?.variant || 'branding';
	const device = useDevice();

	const wrapperRef = useRef<HTMLDivElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const bgRef = useRef<HTMLDivElement>(null);
	const breadcrumbsRef = useRef<HTMLDivElement>(null);

	const resolvedVideo =
		variant === 'creative-marketing-partnership'
			? CREATIVE_MARKETING_PARTNERSHIP_VIDEO_SOURCES
			: BRANDING_VIDEO_SOURCES;

	useViewportVideosPlayback([wrapperRef]);

	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		const video = videoRef.current;

		if (!wrapper || !video) return;

		const subtitle = wrapper.querySelector(`.${css.subtitle}`);
		const titleLines = wrapper.querySelectorAll(`.${css.title} p`);
		const description = wrapper.querySelector(`.${css.description}`);
		const bottom = wrapper.querySelector(`.${css.bottomContainer}`);
		const breadcrumbs = breadcrumbsRef.current;
		const revealEls = [subtitle, ...titleLines, description, bottom].filter(Boolean);

		const bg = bgRef.current;

		const ctx = gsap.context(() => {
			if (bg) {
				gsap.set(bg, { scale: 1.02, yPercent: 0 });
				gsap.to(bg, {
					yPercent: -8,
					scale: 1.1,
					ease: 'none',
					scrollTrigger: {
						trigger: wrapper,
						start: 'top top',
						end: 'bottom top',
						scrub: true
					}
				});
			}

			const tl = gsap.timeline({
				defaults: { ease: 'power3.out', duration: 1.2 },
				scrollTrigger: {
					trigger: wrapper,
					start: 'top 80%',
					toggleActions: 'play none none reverse'
				}
			});

			gsap.set(video, { opacity: 0, scale: 1.1 });
			if (revealEls.length)
				gsap.set(revealEls, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			if (breadcrumbs)
				gsap.set(breadcrumbs, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			tl.to(video, {
				opacity: 1,
				scale: 1,
				duration: 1.8,
				ease: 'power2.out'
			});

			if (subtitle)
				tl.to(
					subtitle,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power3.out'
					},
					'-=1.2'
				);

			tl.to(
				titleLines,
				{
					opacity: 1,
					y: 0,
					filter: 'blur(0px)',
					stagger: 0.15,
					duration: 1.1,
					ease: 'power3.out'
				},
				'-=1'
			)
				.to(
					description,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				)
				.to(
					bottom,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);

			if (breadcrumbs)
				tl.to(
					breadcrumbs,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);
		}, wrapper);

		return () => ctx.revert();
	}, []);

	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		video.load();
		video.play().catch(() => {});
	}, [device]);

	return (
		<section
			ref={wrapperRef}
			className={clsx(
				'wrapper',
				css.wrapper,
				variant === 'creative-marketing-partnership' ? css.withoutFilter : ''
			)}
		>
			{device === 'mobile' && variant === 'creative-marketing-partnership' ? (
				<div
					ref={bgRef}
					className={css.background}
					style={{
						backgroundImage: `url('${resolvedVideo.mobileImg}')`
					}}
				/>
			) : (
				<video
					ref={videoRef}
					autoPlay
					loop
					playsInline
					muted
					controls={false}
					className={css.video}
					preload="none"
					poster={resolvedVideo.poster}
				>
					{VIDEO_SOURCE_META.map(({ key, type }) => {
						const src = resolvedVideo[key];
						return src ? <source key={key} src={src} type={type} /> : null;
					})}
				</video>
			)}

			<div className={css.content}>
				{breadcrumbItems ? (
					<PageBreadcrumbs
						ref={breadcrumbsRef}
						items={breadcrumbItems}
						wrapperClassName={css.breadcrumbsWrap}
						className={css.breadcrumbs}
					/>
				) : null}
				{data.subtitle && (!breadcrumbItems || breadcrumbItems.length <= 1) ? (
					<Typography uppercase variant="body-m" tag="p" className={css.subtitle}>
						{data.subtitle}
					</Typography>
				) : null}
				<Typography uppercase variant="h1" tag="h1" className={css.title}>
					<StrapiRichContent content={data.title} />
				</Typography>
				{data.description ? (
					<Typography variant="body-m" tag="p" className={css.description}>
						{data.description}
					</Typography>
				) : null}
			</div>

			<div className={css.bottomContainer}>
				<div className={css.sliderWrapper}>
					<CustomSlider items={data?.slider || []} />
				</div>
			</div>
		</section>
	);
};

export default BrandingHero;
